import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Link
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import BuildIcon from '@mui/icons-material/Build';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import SecurityIcon from '@mui/icons-material/Security';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import logo0 from '../assets/logo0.png'; // Import the logo correctly

const Admin = () => {
  const adminFeatures = [
    {
      title: "Dashboard Overview",
      icon: <DashboardIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Platform statistics and system health monitoring",
      stats: {
        activeUsers: "2.5K",
        pendingRepairs: "150",
        uptime: "99.9%"
      }
    },
    {
      title: "User Management",
      icon: <PeopleIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Manage users, vendors, and technicians",
      stats: {
        totalUsers: "10K",
        pendingVerifications: "45",
        activeVendors: "120"
      }
    },
    {
      title: "AI Diagnostics",
      icon: <BuildIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Monitor and manage AI repair diagnostics",
      stats: {
        accuracy: "95%",
        dailyDiagnoses: "500+",
        successRate: "92%"
      }
    },
    {
      title: "Marketplace",
      icon: <StorefrontIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Manage transactions and product listings",
      stats: {
        revenue: "$45K",
        activeListings: "2.5K",
        disputes: "12"
      }
    },
    {
      title: "Customer Support",
      icon: <SupportAgentIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Manage support tickets and chatbot",
      stats: {
        openTickets: "75",
        responseRate: "98%",
        satisfaction: "4.8/5"
      }
    },
    {
      title: "Video Consultations",
      icon: <VideoCallIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Manage expert consultation sessions",
      stats: {
        scheduled: "45",
        completed: "380",
        rating: "4.9/5"
      }
    },
    {
      title: "Security",
      icon: <SecurityIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Monitor platform security and fraud prevention",
      stats: {
        threats: "0",
        securityScore: "98%",
        monitoring: "24/7"
      }
    },
    {
      title: "Content Management",
      icon: <ArticleIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Manage blog posts and documentation",
      stats: {
        articles: "250+",
        guides: "120",
        views: "15K"
      }
    },
    {
      title: "Analytics",
      icon: <BarChartIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Platform performance and user analytics",
      stats: {
        metrics: "100+",
        reports: "15",
        insights: "Daily"
      }
    },
    {
      title: "Settings",
      icon: <SettingsIcon sx={{ fontSize: 40, color: '#000' }} />,
      description: "Platform configuration and preferences",
      stats: {
        configs: "50+",
        regions: "25",
        languages: "12"
      }
    }
  ];

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#000', color: '#fff', pt: 8, pb: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 4 }}>
            <RouterLink to="/admin">
              <img 
                src={logo0}
                alt="Larvarris AI"
                style={{ 
                  height: '35px',
                  marginBottom: '16px',
                  filter: 'brightness(0) invert(1)',
                  cursor: 'pointer'
                }}
              />
            </RouterLink>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 2
              }}
            >
              Admin Dashboard
            </Typography>
          </Box>
          <Typography 
            variant="h6" 
            sx={{ 
              maxWidth: '800px',
              opacity: 0.8,
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Complete control center for managing Larvarris AI platform operations, users, and services.
          </Typography>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={3}>
          {adminFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 0,
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <IconButton 
                      sx={{ 
                        mr: 2,
                        color: '#0073EC',
                        bgcolor: 'rgba(0, 115, 236, 0.1)',
                        '&:hover': {
                          bgcolor: 'rgba(0, 115, 236, 0.2)'
                        }
                      }}
                    >
                      {feature.icon}
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                    {feature.description}
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                    {Object.entries(feature.stats).map(([key, value]) => (
                      <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                        </Typography>
                        <Typography variant="body2" fontWeight="600">
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* Added Overview Link */}
                  <Box sx={{ mt: 3, textAlign: 'right' }}>
                    <Link
                      href="#"
                      sx={{
                        color: '#0073EC',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Overview →
                    </Link>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Quick Actions */}
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            {['Approve Vendors', 'Review Reports', 'Manage Users', 'System Health'].map((action) => (
              <Grid item xs={12} sm={6} md={3} key={action}>
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{
                    py: 2,
                    borderColor: '#0073EC',
                    color: '#0073EC',
                    '&:hover': {
                      borderColor: '#0073EC',
                      bgcolor: 'rgba(0, 115, 236, 0.1)'
                    }
                  }}
                >
                  {action}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
};

export default Admin; 